import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { logger } from "../../../lib/logger";
import { useBiz, errText } from "./i18n";
import { Sheet, Field, Empty, Card, inp, btnMain, btnIcon } from "./ui";

const blank = { name: "", qty: "", unit: "", cost: "", batch_id: "", date: new Date().toISOString().slice(0, 10) };

const ExpenseForm = ({ x, batches, t, onClose, onSaved }) => {
  const [f, setF] = useState(x ? { ...blank, ...x, qty: x.qty ?? "", batch_id: x.batch_id || "" } : blank);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { name: f.name, qty: f.qty === "" ? null : f.qty, unit: f.unit, cost: f.cost, batch_id: f.batch_id || null, date: f.date };
    try {
      if (x) await api.put(`/admin/simple/expenses/${x.id}`, body);
      else await api.post("/admin/simple/expenses", body);
      toast.success(t("saved"));
      onSaved();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet title={x ? t("edit") : t("add_expense")} onClose={onClose} testid="expense-form">
      <form onSubmit={save} className="grid grid-cols-2 gap-3">
        <Field label={t("name")} className="col-span-2"><input required value={f.name} onChange={set("name")} data-testid="expense-name" className={inp} /></Field>
        <Field label={t("qty")}><input type="number" step="any" min="0" value={f.qty} onChange={set("qty")} data-testid="expense-qty" className={inp} /></Field>
        <Field label={t("unit")}><input value={f.unit} onChange={set("unit")} placeholder="ml, grams, pieces" data-testid="expense-unit" className={inp} /></Field>
        <Field label={t("cost")}><input required type="number" step="any" min="0" value={f.cost} onChange={set("cost")} data-testid="expense-cost" className={inp} /></Field>
        <Field label={t("date")}><input type="date" value={f.date || ""} onChange={set("date")} data-testid="expense-date" className={inp} /></Field>
        <Field label={t("batch")} className="col-span-2">
          <select value={f.batch_id} onChange={set("batch_id")} data-testid="expense-batch" className={inp}>
            <option value="">{t("general")}</option>
            {batches.map((b) => <option key={b.id} value={b.id}>{b.batch_number}</option>)}
          </select>
        </Field>
        <button disabled={busy} data-testid="expense-save" className={`${btnMain} col-span-2 mt-2`}>{t("save")}</button>
      </form>
    </Sheet>
  );
};

export function ExpensesList() {
  const { t, aed } = useBiz();
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/simple/expenses").then((r) => setD(r.data)).catch((e) => logger.error("expenses", e)), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (x) => {
    if (!window.confirm(t("confirm_delete_exp"))) return;
    try {
      await api.delete(`/admin/simple/expenses/${x.id}`);
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };
  if (!d) return <p className="text-[#8A8173]">…</p>;
  const total = d.expenses.reduce((s, x) => s + Number(x.cost || 0), 0);
  return (
    <div data-testid="biz-expenses">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <button onClick={() => setEdit({})} data-testid="expense-new" className={btnMain}><Plus size={16} />{t("add_expense")}</button>
        <p className="text-sm text-[#6B645A]">{t("total_expenses")}: <span className="font-semibold text-[#1C1A17]" dir="ltr" data-testid="expenses-total">{aed(total)}</span></p>
      </div>
      {d.expenses.length === 0 ? <Empty>{t("no_expenses")}</Empty> : (
        <Card className="!p-0 divide-y divide-[#F1EBE1]" testid="expenses-list">
          {d.expenses.map((x) => (
            <div key={x.id} className="flex items-center gap-3 px-4 py-3" data-testid={`expense-${x.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-[15px] truncate">{x.name}</p>
                <p className="text-xs text-[#8A8173]">{x.qty != null ? `${x.qty} ${x.unit || ""} · ` : ""}{x.batch_number || t("general")}{x.date ? ` · ${x.date}` : ""}</p>
              </div>
              <span className="font-semibold whitespace-nowrap" dir="ltr">{aed(x.cost)}</span>
              <button onClick={() => setEdit({ x })} data-testid={`expense-edit-${x.id}`} className={btnIcon}><Pencil size={15} /></button>
              <button onClick={() => remove(x)} data-testid={`expense-del-${x.id}`} className={btnIcon}><Trash2 size={15} /></button>
            </div>
          ))}
        </Card>
      )}
      {edit && <ExpenseForm x={edit.x} batches={d.batches} t={t} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
